import React, { useMemo } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import AppToggle from '../../../../components/AppToggle'
import {
  StepHeader,
  StepCard,
  DROPDOWN_TRIGGER_CLASS,
} from '../components/StepLayout'

const ORDER_OPTIONS = [
  { value: 'ASC', label: 'Ascending' },
  { value: 'DESC', label: 'Descending' },
]

const SORT_DIR_OPTIONS = [
  { value: 'ASC', label: 'Ascending (A-Z, 0-9)' },
  { value: 'DESC', label: 'Descending (Z-A, 9-0)' },
]

export default function Step5GroupingSorting({
  grouping = [],
  setGrouping,
  sorting = [],
  setSorting,
  selectedFields = [],
  allFields = [],
}) {
  const availableFields = selectedFields.length > 0 ? selectedFields : allFields

  const fieldOptions = useMemo(
    () => availableFields.map((f) => ({ value: f.key, label: f.label })),
    [availableFields]
  )

  function addGrouping() {
    const firstField = availableFields[0]?.key || ''
    setGrouping((prev) => [
      ...prev,
      { field: firstField, order: 'ASC', showSubtotals: true },
    ])
  }

  function removeGrouping(index) {
    setGrouping((prev) => prev.filter((_, i) => i !== index))
  }

  function updateGrouping(index, prop, value) {
    setGrouping((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [prop]: value } : g))
    )
  }

  function addSorting() {
    const firstField = availableFields[0]?.key || ''
    setSorting((prev) => [...prev, { field: firstField, direction: 'ASC' }])
  }

  function removeSorting(index) {
    setSorting((prev) => prev.filter((_, i) => i !== index))
  }

  function updateSorting(index, prop, value) {
    setSorting((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [prop]: value } : s))
    )
  }

  return (
    <div className="space-y-3 max-w-4xl mx-auto py-1">
      <StepHeader
        title="Grouping & Sorting"
        subtitle="Structure data hierarchically with automatic subtotals and define multi-level sorting."
      />

      {/* ── Grouping Section ── */}
      <StepCard
        title="Group Rows By"
        subtitle="Collapse matching rows into hierarchical group blocks (e.g., Department → Project)."
        actions={
          <button
            type="button"
            onClick={addGrouping}
            className="h-8 text-xs font-bold text-primary border border-primary/30 px-2.5 rounded-[8px] hover:bg-primary/5 flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add Group Level
          </button>
        }
      >
        {grouping.length === 0 ? (
          <div className="text-center py-5 border border-dashed border-surface-container-high rounded-[8px] text-xs text-on-surface-variant">
            No grouping applied. Report will display flat tabular rows.
          </div>
        ) : (
          <div className="space-y-2">
            {grouping.map((grp, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-2.5 p-2.5 bg-surface rounded-[8px] border border-surface-container-high text-xs"
              >
                <span className="font-bold text-primary shrink-0 text-xs w-16">Level {index + 1}</span>
                <div className="flex-1 min-w-0">
                  <AppDropdown
                    value={grp.field}
                    onChange={(val) => updateGrouping(index, 'field', val)}
                    options={fieldOptions}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <div className="w-full sm:w-36 shrink-0">
                  <AppDropdown
                    value={grp.order}
                    onChange={(val) => updateGrouping(index, 'order', val)}
                    options={ORDER_OPTIONS}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <div className="flex items-center gap-2 px-2 shrink-0">
                  <span className="text-xs text-on-surface select-none font-medium">Subtotals</span>
                  <AppToggle
                    checked={grp.showSubtotals}
                    onChange={(checked) => updateGrouping(index, 'showSubtotals', checked)}
                    ariaLabel="Toggle Subtotals"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeGrouping(index)}
                  className="p-1.5 text-slate-400 hover:text-error hover:bg-surface-container rounded-[8px] self-end sm:self-center shrink-0 transition-colors"
                  title="Remove Group Level"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </StepCard>

      {/* ── Sorting Section ── */}
      <StepCard
        title="Sorting Precedence"
        subtitle="Order records sequentially across chosen fields."
        actions={
          <button
            type="button"
            onClick={addSorting}
            className="h-8 text-xs font-bold text-primary border border-primary/30 px-2.5 rounded-[8px] hover:bg-primary/5 flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add Sort Field
          </button>
        }
      >
        {sorting.length === 0 ? (
          <div className="text-center py-5 border border-dashed border-surface-container-high rounded-[8px] text-xs text-on-surface-variant">
            Default sorting by primary identifier (descending).
          </div>
        ) : (
          <div className="space-y-2">
            {sorting.map((sort, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-2.5 p-2.5 bg-surface rounded-[8px] border border-surface-container-high text-xs"
              >
                <span className="font-bold text-primary shrink-0 text-xs w-16">Priority {index + 1}</span>
                <div className="flex-1 min-w-0">
                  <AppDropdown
                    value={sort.field}
                    onChange={(val) => updateSorting(index, 'field', val)}
                    options={fieldOptions}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <div className="w-full sm:w-48 shrink-0">
                  <AppDropdown
                    value={sort.direction}
                    onChange={(val) => updateSorting(index, 'direction', val)}
                    options={SORT_DIR_OPTIONS}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeSorting(index)}
                  className="p-1.5 text-slate-400 hover:text-error hover:bg-surface-container rounded-[8px] self-end sm:self-center shrink-0 transition-colors"
                  title="Remove Sort Field"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </StepCard>
    </div>
  )
}
